import { useMutation } from "@tanstack/react-query";
import { QueryKeys, client, queryClient } from "queryClient";
import { EffortRule } from "types";

export const useEffortRuleDelete = () => {
  const deleteMutation = useMutation({
    mutationFn: async (effortRule: EffortRule) => {
      const { data } = await client.models.EffortRule.delete({
        id: effortRule.id,
      });

      if (!data) return null;

      return effortRule;
    },
    // When mutate is called:
    onMutate: async (newEffortRule) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.EffortRules, newEffortRule.id],
      });

      await queryClient.cancelQueries({
        queryKey: [QueryKeys.EffortRules],
      });

      // Snapshot the previous value
      const previousEffortRule = queryClient.getQueryData([
        QueryKeys.EffortRules,
        newEffortRule.id,
      ]);

      // Optimistically update to the new value
      if (previousEffortRule) {
        queryClient.setQueryData(
          [QueryKeys.EffortRules, newEffortRule.id],
          /**
           * `newEffortRule` will at first only include updated values for
           * the record. To avoid only rendering optimistic values for updated
           * fields on the UI, include the previous values for all fields:
           */
          { ...previousEffortRule, ...newEffortRule },
        );
      }

      // Return a context with the previous and new effortRule
      return {
        previousEffortRule,
        newEffortRule,
      };
    },
    // If the mutation fails, use the context we returned above
    onError: (err, newEffortRule, context) => {
      console.error("Error updating record:", err, newEffortRule);
      if (context?.previousEffortRule) {
        queryClient.setQueryData(
          [QueryKeys.EffortRules, context.newEffortRule.id],
          context.previousEffortRule,
        );
      }
    },
    // Always refetch after error or success:
    onSettled: (newEffortRule) => {
      if (newEffortRule) {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.EffortRules, newEffortRule.id],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.EffortRules],
        });
      }
    },
  });

  return deleteMutation;
};
